var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, TextField } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationType } from '../../../enum';
import { AddressPlacesAutocomplete, Autocomplete, ErrorMessageBox, FallbackComponent, NumberInput, UnControlledFormField } from '../../common';
import { MAX_TEXT_LENGTH } from '../constants';
import { useLocationDetailAPI, useUpdateLocationAPI } from '../selectors';
import { resolveTotalSeats, useLocationFormState } from './useLocationFormState';
export var LocationEditModal = function (_a) {
  var _b;
  var open = _a.open,
    onClose = _a.onClose,
    locationId = _a.locationId,
    onSaved = _a.onSaved;
  var apiKey = (_b = process.env.REACT_APP_GOOGLE_PLACES_API_KEY) !== null && _b !== void 0 ? _b : '';
  var t = useTranslation('orgConfig', {
    keyPrefix: 'orgConfigWizard.locationsStep'
  }).t;
  var _c = useLocationDetailAPI(locationId),
    locationDetailData = _c[0],
    fetchLocationDetail = _c[1];
  var _d = useUpdateLocationAPI(locationId !== null && locationId !== void 0 ? locationId : 0),
    updateData = _d[0],
    updateLocation = _d[1],
    clearUpdateData = _d[2];
  var form = useLocationFormState();
  var address = form.address,
    setAddress = form.setAddress,
    locationName = form.locationName,
    setLocationName = form.setLocationName,
    locationType = form.locationType,
    setLocationType = form.setLocationType,
    unlimitedSeats = form.unlimitedSeats,
    setUnlimitedSeats = form.setUnlimitedSeats,
    totalSeats = form.totalSeats,
    setTotalSeats = form.setTotalSeats,
    city = form.city,
    setCity = form.setCity,
    region = form.region,
    setRegion = form.setRegion,
    stateProvince = form.stateProvince,
    setStateProvince = form.setStateProvince,
    stateProvinceCode = form.stateProvinceCode,
    setStateProvinceCode = form.setStateProvinceCode,
    countryName = form.countryName,
    setCountryName = form.setCountryName,
    countryCode = form.countryCode,
    setCountryCode = form.setCountryCode,
    addressCountryResolved = form.addressCountryResolved,
    setAddressCountryResolved = form.setAddressCountryResolved,
    latitude = form.latitude,
    setLatitude = form.setLatitude,
    longitude = form.longitude,
    setLongitude = form.setLongitude;
  var location = locationDetailData.data;
  useEffect(function () {
    if (open && locationId) {
      fetchLocationDetail();
    }
  }, [open, locationId, fetchLocationDetail]);
  useEffect(function () {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if (location) {
      setAddress((_a = location.address) !== null && _a !== void 0 ? _a : '');
      setLocationName(location.locationName);
      setLocationType((_b = location.locationType) !== null && _b !== void 0 ? _b : LocationType.InHouse);
      setUnlimitedSeats(location.totalSeats == null);
      setTotalSeats((_c = location.totalSeats) !== null && _c !== void 0 ? _c : '');
      setCity((_d = location.city) !== null && _d !== void 0 ? _d : '');
      setRegion((_e = location.region) !== null && _e !== void 0 ? _e : '');
      setStateProvince((_f = location.stateProvince) !== null && _f !== void 0 ? _f : '');
      setStateProvinceCode((_g = location.stateProvinceCode) !== null && _g !== void 0 ? _g : null);
      setCountryName((_h = location.countryName) !== null && _h !== void 0 ? _h : '');
      setCountryCode((_j = location.countryCode) !== null && _j !== void 0 ? _j : null);
      setAddressCountryResolved(Boolean(location.countryCode && location.address));
      setLatitude(null);
      setLongitude(null);
    }
  }, [location, setAddress, setCity, setCountryName, setCountryCode, setAddressCountryResolved, setLatitude, setLocationName, setLocationType, setLongitude, setRegion, setStateProvince, setStateProvinceCode, setTotalSeats, setUnlimitedSeats]);
  useEffect(function () {
    if (updateData.data) {
      onSaved();
      onClose();
    }
    return function () {
      clearUpdateData();
    };
  }, [updateData.data, onSaved, onClose, clearUpdateData]);
  var handleAddressChange = function (value, components) {
    var _a, _b, _c, _d, _e;
    setAddress(value);
    if (components.city) setCity(components.city);
    if (components.stateProvince) setStateProvince((_a = components.stateProvince) !== null && _a !== void 0 ? _a : '');
    setStateProvinceCode((_b = components.stateProvinceCode) !== null && _b !== void 0 ? _b : null);
    if (components.countryName) {
      setCountryName(components.countryName);
      setCountryCode((_c = components.countryCode) !== null && _c !== void 0 ? _c : null);
      setAddressCountryResolved(true);
    } else {
      setCountryName('');
      setCountryCode(null);
      setAddressCountryResolved(false);
    }
    setLatitude((_d = components.latitude) !== null && _d !== void 0 ? _d : null);
    setLongitude((_e = components.longitude) !== null && _e !== void 0 ? _e : null);
  };
  var handleSave = function () {
    var _a, _b, _c;
    if (!locationId) return;
    updateLocation(__assign({
      address: (address === null || address === void 0 ? void 0 : address.trim()) || null,
      locationName: locationName === null || locationName === void 0 ? void 0 : locationName.trim(),
      locationType: locationType,
      totalSeats: resolveTotalSeats(unlimitedSeats, totalSeats),
      city: (city === null || city === void 0 ? void 0 : city.trim()) || null,
      stateProvince: (stateProvince === null || stateProvince === void 0 ? void 0 : stateProvince.trim()) || null,
      stateProvinceCode: (_a = stateProvinceCode === null || stateProvinceCode === void 0 ? void 0 : stateProvinceCode.trim()) !== null && _a !== void 0 ? _a : null,
      countryName: (countryName === null || countryName === void 0 ? void 0 : countryName.trim()) || ((_b = location === null || location === void 0 ? void 0 : location.countryName) === null || _b === void 0 ? void 0 : _b.trim()) || '',
      countryCode: (_c = countryCode === null || countryCode === void 0 ? void 0 : countryCode.trim()) !== null && _c !== void 0 ? _c : null
    }, latitude != null && longitude != null ? {
      latitude: latitude,
      longitude: longitude
    } : {}));
  };
  if (!locationId) return null;
  // Hide Country when Google Places resolves it to prevent manual override of API data; show as fallback when API is unavailable or address does not resolve.
  var showCountryField = !apiKey || !addressCountryResolved;
  var disableSaveButton = !!updateData.requesting || !(locationName === null || locationName === void 0 ? void 0 : locationName.trim());
  return _jsxs(Dialog, __assign({
    open: open,
    onClose: onClose,
    maxWidth: "sm",
    fullWidth: true
  }, {
    children: [_jsx(DialogTitle, __assign({
      variant: "h2"
    }, {
      children: t('editTitle', {
        defaultValue: 'Edit'
      })
    })), _jsx(DialogContent, {
      children: _jsx(Box, __assign({
        sx: {
          pt: 1
        }
      }, {
        children: _jsxs(FallbackComponent, __assign({
          requesting: !!locationDetailData.requesting,
          overlay: true
        }, {
          children: [_jsx(ErrorMessageBox, {
            errors: [updateData.error]
          }), _jsxs(Stack, __assign({
            spacing: 2,
            direction: 'column'
          }, {
            children: [_jsx(UnControlledFormField, __assign({
              label: t('address')
            }, {
              children: _jsx(AddressPlacesAutocomplete, {
                apiKey: apiKey,
                value: address,
                onChange: handleAddressChange,
                editMode: true,
                fullWidth: true,
                placeholder: t('addressSearchPlaceholder', {
                  defaultValue: 'Search to replace address…'
                })
              })
            })), _jsx(UnControlledFormField, __assign({
              label: t('siteLocationName'),
              required: true
            }, {
              children: _jsx(TextField, {
                fullWidth: true,
                value: locationName,
                onChange: function (e) {
                  return setLocationName(e.target.value);
                },
                placeholder: t('siteLocationNamePlaceholder', {
                  defaultValue: 'Site Location Name'
                }),
                inputProps: {
                  maxLength: MAX_TEXT_LENGTH
                }
              })
            })), _jsx(UnControlledFormField, __assign({
              label: t('locationType'),
              required: true
            }, {
              children: _jsx(Autocomplete, {
                options: [{
                  value: LocationType.InHouse,
                  label: t('locationTypeInHouse', {
                    defaultValue: 'In-House'
                  })
                }, {
                  value: LocationType.Outsourced,
                  label: t('locationTypeOutsourced', {
                    defaultValue: 'Outsourced'
                  })
                }],
                getOptionValue: function (o) {
                  return o.value;
                },
                getOptionLabel: function (o) {
                  return o.label;
                },
                value: locationType,
                onChange: function (value) {
                  return setLocationType(value);
                }
              })
            })), _jsx(FormControlLabel, {
              control: _jsx(Checkbox, {
                checked: unlimitedSeats,
                onChange: function (e) {
                  var checked = e.target.checked;
                  setUnlimitedSeats(checked);
                  setTotalSeats(checked ? '' : 1);
                },
                disableRipple: true,
                sx: {
                  pl: 0
                }
              }),
              label: t('unlimitedSeats')
            }), _jsx(UnControlledFormField, __assign({
              label: t('numberOfSeats')
            }, {
              children: _jsx(NumberInput, {
                min: 1,
                max: 9999999,
                value: totalSeats === '' ? null : Number(totalSeats),
                onChange: function (v) {
                  return setTotalSeats(v == null ? '' : Number(v));
                },
                disabled: unlimitedSeats
              })
            })), _jsx(UnControlledFormField, __assign({
              label: t('city')
            }, {
              children: _jsx(TextField, {
                fullWidth: true,
                value: city,
                onChange: function (e) {
                  return setCity(e.target.value);
                },
                inputProps: {
                  maxLength: MAX_TEXT_LENGTH
                }
              })
            })), _jsx(UnControlledFormField, __assign({
              label: t('region')
            }, {
              children: _jsx(TextField, {
                fullWidth: true,
                value: region,
                InputProps: {
                  readOnly: true
                },
                inputProps: {
                  maxLength: MAX_TEXT_LENGTH
                }
              })
            })), _jsx(UnControlledFormField, __assign({
              label: t('stateProvince')
            }, {
              children: _jsx(TextField, {
                fullWidth: true,
                value: stateProvince,
                onChange: function (e) {
                  return setStateProvince(e.target.value);
                },
                inputProps: {
                  maxLength: MAX_TEXT_LENGTH
                }
              })
            })), showCountryField && _jsx(UnControlledFormField, __assign({
              label: t('country')
            }, {
              children: _jsx(TextField, {
                fullWidth: true,
                value: countryName,
                onChange: function (e) {
                  setCountryName(e.target.value);
                  setCountryCode(null);
                  setAddressCountryResolved(false);
                },
                inputProps: {
                  maxLength: MAX_TEXT_LENGTH
                }
              })
            }))]
          }))]
        }))
      }))
    }), _jsxs(DialogActions, {
      children: [_jsx(Button, __assign({
        variant: "outlined",
        onClick: onClose
      }, {
        children: t('cancel')
      })), _jsx(Button, __assign({
        variant: "contained",
        onClick: handleSave,
        disabled: disableSaveButton
      }, {
        children: t('save')
      }))]
    })]
  }));
};